import {CSSProperties, forwardRef, useEffect, useMemo, useRef, useState} from "react";
import classNames from "classnames";

import {ManualPopover} from "@pg-design/floating-module";
import {SlimRange} from "@pg-design/range-module";

import {MAX_VALUE_INPUT_LIMITER} from "../../constants/max_value_input_limiter";
import {IMaxValueInputProps} from "../../types/IMaxValueInputProps";
import {getNumberFormat} from "../../utils/get_number_format";
import {Input} from "../input/Input";

import * as cns from "./MaxValueInput.module.css";

export const MaxValueInput = forwardRef<HTMLInputElement, IMaxValueInputProps>((props, forwardedRef) => {
    const {
        step,
        min,
        max,
        name,
        value,
        onChange,
        inputVariant = "row",
        isDynamicStep = false,
        defaultValue,
        inputClassName,
        maxDisplayLabel,
        centerValue,
        ...inputProps
    } = props;

    const [displayValue, setDisplayValue] = useState<string>(getNumberFormat(Number(value || defaultValue || min)));
    const [warning, setWarning] = useState({isOpen: false, message: ""});
    const [inputWidth, setInputWidth] = useState(`${displayValue.toString().length}ch`);

    const warningHideRef = useRef<null | ReturnType<typeof setTimeout>>(null);
    const measureSpanRef = useRef<null | HTMLSpanElement>(null);
    const rangeValue = useMemo(() => {
        if (maxDisplayLabel && displayValue === maxDisplayLabel) {
            return {
                upper: max
            };
        }
        const noWs = getWithoutWhiteSpace(displayValue);

        return {
            upper: parseInt(noWs)
        };
    }, [displayValue]);

    useEffect(() => {
        if (maxDisplayLabel) {
            const numValue = parseInt(getWithoutWhiteSpace(displayValue));
            (numValue === max || numValue === MAX_VALUE_INPUT_LIMITER) && setDisplayValue(maxDisplayLabel);
        }
        const hiddenSpanObserver = new ResizeObserver((entries) => {
            for (const entry of entries) {
                setInputWidth(`${entry.contentRect.width}px`);
            }
        });

        if (measureSpanRef.current) {
            hiddenSpanObserver.observe(measureSpanRef.current);
        }

        return () => {
            hiddenSpanObserver.disconnect();
        };
    }, []);

    useEffect(() => {
        if (warning.isOpen) {
            warningHideRef.current = setTimeout(() => {
                setWarning({isOpen: false, message: warning.message});
            }, 2000);
        }

        return () => {
            if (warningHideRef.current) {
                clearTimeout(warningHideRef.current);
                warningHideRef.current = null;
            }
        };
    }, [warning.isOpen]);

    useEffect(() => {
        if (onChange) {
            if (displayValue === maxDisplayLabel) {
                onChange(MAX_VALUE_INPUT_LIMITER);
                return;
            }
            const numValue = getWithoutWhiteSpace(displayValue);
            onChange(parseInt(numValue));
        }
    }, [displayValue]);

    return (
        <div
            className={classNames(cns.maxValueInput, inputVariant === "column" && cns.maxValueInputColumn)}
            style={
                {
                    "--input-width": inputWidth
                } as CSSProperties
            }
        >
            <SlimRange
                name={`${name}_range`}
                value={rangeValue}
                useDynamicChange
                onChange={(_, value) => {
                    if (value.upper || value.upper === 0) {
                        maxDisplayLabel && value.upper === max ? setDisplayValue(maxDisplayLabel) : setDisplayValue(getNumberFormat(Number(value.upper)));
                    }
                }}
                step={step}
                min={min}
                max={max}
                className={classNames(cns.maxValueInputRange, inputProps.disabled && cns.maxValueInputRangeDisabled)}
                hideLabels
                disabled={inputProps.disabled}
                centerValue={centerValue}
                useStepChange={isDynamicStep}
                useTriColorSlider={props.useTriColorSlider}
            />
            <ManualPopover
                popover={warning.message}
                isOpen={warning.isOpen}
                type="warning"
                handleSetIsOpen={(isWarningOpen) => {
                    setWarning({isOpen: isWarningOpen, message: warning.message});
                }}
                className={cns.maxValueInputPopover}
            >
                {({refs, getReferenceProps}) => (
                    <div
                        ref={refs.setReference}
                        {...getReferenceProps()}
                        className={inputVariant === "column" ? cns.maxValueInputFieldColumn : cns.maxValueInputField}
                    >
                        <span className={classNames(cns.maxValueInputHiddenSpan, inputClassName)} ref={measureSpanRef}>
                            {displayValue}
                        </span>
                        <Input
                            ref={forwardedRef}
                            name={name}
                            {...inputProps}
                            rightElement={
                                displayValue === maxDisplayLabel ? (
                                    <span className={cns.maxValueInputLabelHidden}>{inputProps.rightElement}</span>
                                ) : (
                                    inputProps.rightElement
                                )
                            }
                            value={displayValue}
                            inputWrapClassName={cns.maxValueInputWrapper}
                            inputClassName={classNames(cns.maxValueInputCore, inputClassName)}
                            elementCustomClassName={classNames(
                                cns.maxValueInputCoreRightElement,
                                typeof inputProps.rightElement === "string" && inputProps.disabled && cns.maxValueInputDisabledStringElement
                            )}
                            onBlur={() => {
                                const newValue = displayValue;
                                const valueWithoutWhiteSpace = getWithoutWhiteSpace(newValue);
                                const isValueWithDigitsOnly = isOnlyWithDigits(valueWithoutWhiteSpace);

                                const newValueInt = parseInt(valueWithoutWhiteSpace) | 0;

                                if (!isValueWithDigitsOnly && !warning.isOpen && newValue !== maxDisplayLabel) {
                                    setWarning({
                                        isOpen: true,
                                        message: "Dozwolone są tylko cyfry"
                                    });
                                    setDisplayValue(getNumberFormat(min));
                                    return;
                                }

                                if (isValueWithDigitsOnly && newValueInt < min) {
                                    setWarning({
                                        isOpen: true,
                                        message: `Minimalna wartość: ${min}`
                                    });
                                    setDisplayValue(getNumberFormat(min));
                                    return;
                                }

                                if (isValueWithDigitsOnly && newValueInt > max) {
                                    setWarning({
                                        isOpen: true,
                                        message: `Maksymalna wartość: ${max}`
                                    });
                                    setDisplayValue(maxDisplayLabel || getNumberFormat(max));
                                    return;
                                }
                            }}
                            onChange={(event) => {
                                const newValue = event.currentTarget.value;
                                const valueWithoutWhiteSpace = getWithoutWhiteSpace(newValue);

                                const newValueInt = parseInt(valueWithoutWhiteSpace);

                                if (Number.isNaN(newValueInt)) {
                                    setDisplayValue("");
                                    return;
                                }

                                if (newValueInt > MAX_VALUE_INPUT_LIMITER) {
                                    setDisplayValue(maxDisplayLabel || getNumberFormat(MAX_VALUE_INPUT_LIMITER));
                                    return;
                                }

                                setDisplayValue(maxDisplayLabel && newValueInt === max ? maxDisplayLabel : getNumberFormat(newValueInt));
                            }}
                            onKeyDown={(event) => {
                                if (event.key === "Enter") {
                                    event.stopPropagation();
                                    event.currentTarget.blur();
                                }
                            }}
                        />
                    </div>
                )}
            </ManualPopover>
        </div>
    );
});

// Utils
function getWithoutWhiteSpace(value: string | number) {
    return value.toString().replace(/\s+/g, "");
}

function isOnlyWithDigits(value: string) {
    return value === "" || /^\d+$/.test(value);
}
